/**
 * Hook for loading Copilot thread history.
 *
 * On mount (or when threadId changes from URL param):
 * - Fetches thread list for the sidebar
 * - Loads thread history and replays into store (messages + workspace artifacts)
 */

import { useEffect, useCallback } from 'react';
import { buildApiUrl, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { useCopilotStore } from '../stores/useCopilotStore';
import type {
  CopilotMessage,
  CopilotThread,
  CopilotTimelineEntry,
  QueryResultTab,
  ChartArtifact,
  MetricArtifact,
  Finding,
  WorkspaceArtifact,
  CalculationAuditReport,
} from '../stores/useCopilotStore';

// ============================================================================
// Thread list fetcher
// ============================================================================

export function useFetchThreadList() {
  const setThreadList = useCopilotStore((s: any) => s.setThreadList);

  const fetchThreads = useCallback(async () => {
    try {
      const token = localStorage.getItem('klaris_jwt');
      const tid = getTenantIdFromStorage();
      if (!token || !tid) return;

      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/chat/threads`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;

      const data = await resp.json();
      const threads: CopilotThread[] = (data.threads || [])
        .filter((t: any) => t.agent_type === 'copilot')
        .map((t: any) => ({
          threadId: t.thread_id,
          title: t.title,
          agentType: t.agent_type || 'copilot',
          createdAt: t.created_at || null,
        }));

      setThreadList(threads);
    } catch (e) {
      console.error('[useFetchThreadList] Error:', e);
    }
  }, [setThreadList]);

  return fetchThreads;
}

// ============================================================================
// Thread history loader
// ============================================================================

export function useCopilotHistory(threadId: string | null) {
  const fetchThreads = useFetchThreadList();

  useEffect(() => {
    // Always refresh thread list on mount / threadId change
    fetchThreads();
  }, [threadId, fetchThreads]);

  useEffect(() => {
    if (!threadId) return;

    const initialState = useCopilotStore.getState();

    // If the store is already populated for THIS thread (e.g. user just sent
    // their first message and the history fetch is racing), don't overwrite.
    if (initialState.threadId === threadId && initialState.messages.length > 0) {
      return;
    }

    // Otherwise, we're switching threads (deep-link, shared-space click,
    // browser back/forward, etc.). Wipe the stale session so artifacts and
    // messages from the previous thread don't bleed into the new one.
    if (initialState.threadId !== threadId) {
      initialState.resetSession();
      initialState.setThreadId(threadId);
    }

    const loadHistory = async () => {
      try {
        useCopilotStore.getState().setIsLoadingHistory(true);
        const token = localStorage.getItem('klaris_jwt');
        const tid = getTenantIdFromStorage();
        if (!token || !tid) return;

        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/chat/threads/${threadId}/history`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;

        const data = await resp.json();
        const store = useCopilotStore.getState();

        // Set title from API (same as thread list / LLM-generated first-message title)
        if (data.thread_title) {
          store.setThreadTitle(data.thread_title);
        }

        // Replay events → messages + artifacts + mobile unified timeline (SSE order)
        const messages: CopilotMessage[] = [];
        const artifacts: WorkspaceArtifact[] = [];
        const findings: Finding[] = [];
        const auditReports: Record<string, CalculationAuditReport> = {};
        const timeline: CopilotTimelineEntry[] = [];

        (data.messages || []).forEach((msg: any) => {
          if (msg.role === 'user') {
            const uid = `hist-user-${msg.created_at}-${Math.random().toString(36).slice(2, 6)}`;
            messages.push({
              id: uid,
              role: 'user',
              content: msg.content || '',
              timestamp: msg.created_at,
            });
            timeline.push({ type: 'user', messageId: uid });
          } else if (msg.role === 'assistant' && msg.events) {
            // Accumulate text from agent_message events
            let text = '';
            let messageAuditId: string | undefined;
            const sorted = [...msg.events].sort(
              (a: any, b: any) => a.event_order - b.event_order,
            );

            const flushAssistantText = () => {
              if (!text.trim()) return;
              const mid = `hist-asst-${msg.created_at}-${Math.random().toString(36).slice(2, 6)}`;
              messages.push({
                id: mid,
                role: 'assistant',
                content: text,
                timestamp: msg.created_at,
                auditId: messageAuditId,
              });
              timeline.push({ type: 'assistant', messageId: mid });
              text = '';
            };

            sorted.forEach((evt: any) => {
              // event_data in DB is the full SSE event dict (which itself has a
              // nested `data` field for the actual payload).  Unwrap it.
              const raw = evt.data || {};
              const evtData = raw.data && typeof raw.data === 'object' ? raw.data : raw;

              // Skip companion structured events written for widget-insights
              // compatibility (node="answer"). The canonical artifact events
              // have node="tools" and are handled below.
              if (evt.node === 'answer' && (evt.type === 'chart' || evt.type === 'table_result' || evt.type === 'metric_card')) {
                return;
              }

              switch (evt.type) {
                case 'agent_message': {
                  const chunk = evtData.content || '';
                  if (chunk) text += (text ? '' : '') + chunk;
                  break;
                }

                case 'query_result': {
                  const d = evtData;
                  // 'local' was the legacy category used before analyze_data
                  // frames were made visible. Those stored events have 0 rows
                  // (DuckDB frames don't persist across sessions) so suppress them.
                  if (d.connector_category === 'local') break;
                  flushAssistantText();
                  const tab: QueryResultTab = {
                    id: `hist-qr-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`,
                    type: 'query_result',
                    title: d.title || 'Query Result',
                    columns: d.columns || [],
                    rows: d.rows || [],
                    rowCount: d.row_count || 0,
                    elapsedMs: d.elapsed_ms || 0,
                    sql: (d.sql || '').replace(/\bt_frame_[0-9a-f]+\b/g, 'klaris_stage_layer'),
                    connectorId: d.connector_id || '',
                    connectorName: d.connector_name || '',
                    connectorType: d.connector_type || '',
                    connectorCategory: d.connector_category || '',
                    isFileConnector: d.is_file_connector || false,
                    truncated: d.truncated || false,
                    createdAt: evt.created_at,
                  };
                  artifacts.push(tab);
                  timeline.push({ type: 'artifact', artifactId: tab.id });
                  break;
                }

                case 'chart': {
                  const c = evtData;
                  flushAssistantText();
                  const chart: ChartArtifact = {
                    id: `hist-chart-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`,
                    type: 'chart',
                    title: c.title || 'Chart',
                    chartType: c.chart_intent?.type || c.type || 'bar',
                    spec: c.spec || {},
                    rowCount: c.row_count || 0,
                    columns: c.columns || [],
                    chartIntent: c.chart_intent || {},
                    createdAt: evt.created_at,
                  };
                  artifacts.push(chart);
                  timeline.push({ type: 'artifact', artifactId: chart.id });
                  break;
                }

                case 'metric_card': {
                  const m = evtData;
                  flushAssistantText();
                  const metricArtifact: MetricArtifact = {
                    id: `hist-metric-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`,
                    type: 'metric_card',
                    title: m.title || m.metric_name || 'Metric',
                    metricName: m.metric_name || m.title || '',
                    metricValue: m.metric_value,
                    columns: m.columns || [],
                    rowCount: m.row_count || 0,
                    createdAt: evt.created_at,
                    eventId: evt.event_id || undefined,
                  };
                  artifacts.push(metricArtifact);
                  timeline.push({ type: 'artifact', artifactId: metricArtifact.id });
                  break;
                }

                case 'finding': {
                  const f = evtData;
                  findings.push({
                    id: f.id || `finding-hist-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`,
                    title: f.title || 'Untitled Finding',
                    narrative: f.narrative || '',
                    dataPoint: f.data_point || {},
                    artifactRefs: f.artifact_refs || [],
                    createdAt: f.created_at || evt.created_at,
                  });
                  break;
                }

                case 'knowledge_captured': {
                  flushAssistantText();
                  const kcNodes: Array<{ name: string; node_type: string; definition?: string; sql?: string }> = Array.isArray(evtData)
                    ? evtData
                    : evtData?.name
                    ? [evtData]
                    : [];
                  if (kcNodes.length > 0) {
                    const kcId = `hist-kc-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`;
                    messages.push({
                      id: kcId,
                      role: 'assistant',
                      content: '',
                      timestamp: evt.created_at || msg.created_at,
                      type: 'knowledge_captured' as const,
                      knowledgeItems: kcNodes.map((kc) => ({
                        name: kc.name,
                        node_type: kc.node_type,
                        definition: kc.definition,
                        sql: kc.sql,
                      })),
                    });
                    timeline.push({ type: 'assistant', messageId: kcId });
                  }
                  break;
                }

                case 'sql_approval': {
                  flushAssistantText();
                  timeline.push({
                    type: 'sql_approval',
                    id: `hist-sql-apr-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`,
                  });
                  break;
                }

                case 'calculation_audit': {
                  const a = evtData || {};
                  const auditId = evt.event_id || a.id || `hist-audit-${evt.created_at}-${Math.random().toString(36).slice(2, 6)}`;
                  auditReports[auditId] = {
                    id: auditId,
                    threadId: threadId,
                    question: a.question || '',
                    startedAt: a.started_at || evt.created_at,
                    completedAt: a.completed_at || evt.created_at,
                    showAuditLink: typeof a.show_audit_link === 'boolean' ? a.show_audit_link : undefined,
                    dataSources: (a.data_sources || []).map((s: any) => ({
                      frameId: s.frame_id || '',
                      title: s.title || 'Query Result',
                      connectorId: s.connector_id || '',
                      connectorName: s.connector_name || '',
                      connectorType: s.connector_type || '',
                      rowCount: s.row_count || 0,
                      columns: s.columns || [],
                      sql: s.sql || '',
                      timestamp: s.timestamp || evt.created_at,
                    })),
                    steps: (a.steps || []).map((st: any) => ({
                      type: st.type || '',
                      toolName: st.tool_name || '',
                      toolArgs: st.tool_args || {},
                      summary: st.summary || '',
                      timestamp: st.timestamp || evt.created_at,
                    })),
                    charts: (a.charts || []).map((c: any) => ({
                      title: c.title || 'Chart',
                      chartType: c.chart_type || 'chart',
                      rowCount: c.row_count || 0,
                      columns: c.columns || [],
                      timestamp: c.timestamp || evt.created_at,
                    })),
                    story: a.story
                      ? {
                          headline: a.story.headline || '',
                          dataSources: a.story.data_sources
                            ? {
                                summary: a.story.data_sources.summary || '',
                                connectors: a.story.data_sources.connectors || [],
                                tablesOrEndpoints: a.story.data_sources.tables_or_endpoints || [],
                                details: (a.story.data_sources.details || []).map((d: any) => ({
                                  name: d.name || '',
                                  connectorType: d.connector_type || '',
                                  records: d.records || 0,
                                  columns: d.columns || 0,
                                  frameId: d.frame_id || '',
                                  query: d.query || '',
                                  requestType: d.request_type || 'sql',
                                  tablesOrEndpoints: d.tables_or_endpoints || [],
                                })),
                              }
                            : undefined,
                          staging: a.story.staging
                            ? {
                                summary: a.story.staging.summary || '',
                                stagedRecords: a.story.staging.staged_records || 0,
                                frameCount: a.story.staging.frame_count || 0,
                              }
                            : undefined,
                          calculation: a.story.calculation
                            ? {
                                summary: a.story.calculation.summary || '',
                                sqlStepCount: a.story.calculation.sql_step_count || 0,
                                finalSql: a.story.calculation.final_sql || '',
                                steps: (a.story.calculation.steps || []).map((step: any) => ({
                                  tool: step.tool || '',
                                  sql: step.sql || '',
                                })),
                              }
                            : undefined,
                          outputs: a.story.outputs
                            ? {
                                summary: a.story.outputs.summary || '',
                                resultTypes: a.story.outputs.result_types || [],
                                chartCount: a.story.outputs.chart_count || 0,
                                chartTitles: a.story.outputs.chart_titles || [],
                                chartDetails: (a.story.outputs.chart_details || []).map((c: any) => ({
                                  title: c.title || '',
                                  type: c.type || '',
                                  records: c.records || 0,
                                  columns: c.columns || [],
                                  xFields: c.x_fields || [],
                                  yFields: c.y_fields || [],
                                  timeUnit: c.time_unit || '',
                                })),
                                forecastCount: a.story.outputs.forecast_count ?? 0,
                                forecastDetails: (a.story.outputs.forecast_details || []).map((f: any) => ({
                                  title: f.title || '',
                                  frameId: f.frame_id || '',
                                  selectedModel: f.selected_model || '',
                                  horizon: f.horizon ?? 0,
                                  timeUnit: f.time_unit || '',
                                  confidenceLevel: f.confidence_level,
                                  confidenceLevelPct: f.confidence_level_pct,
                                  reliability: f.reliability || '',
                                  historyPoints: f.history_points ?? 0,
                                  targetColumn: f.target_column || '',
                                  timeColumn: f.time_column || '',
                                  periodsCount: f.periods_count ?? 0,
                                  firstPeriod: f.first_period
                                    ? {
                                        period: f.first_period.period,
                                        prediction: f.first_period.prediction,
                                        lower: f.first_period.lower,
                                        upper: f.first_period.upper,
                                      }
                                    : undefined,
                                  candidateModels: (f.candidate_models || []).map((m: any) => ({
                                    model: m.model || '',
                                    mae: m.mae ?? null,
                                    rmse: m.rmse ?? null,
                                    wape: m.wape ?? null,
                                    p95AbsError: m.p95_abs_error ?? null,
                                    selectionScore: m.selection_score ?? null,
                                  })),
                                  selectedModelMetrics: f.selected_model_metrics
                                    ? {
                                        mae: f.selected_model_metrics.mae ?? null,
                                        rmse: f.selected_model_metrics.rmse ?? null,
                                        wape: f.selected_model_metrics.wape ?? null,
                                        p95AbsError: f.selected_model_metrics.p95_abs_error ?? null,
                                        selectionScore: f.selected_model_metrics.selection_score ?? null,
                                      }
                                    : undefined,
                                  caveats: f.caveats || [],
                                  dataWindowStart: f.data_window_start || '',
                                  dataWindowEnd: f.data_window_end || '',
                                  droppedPoints: f.dropped_points ?? 0,
                                  rawRows: f.raw_rows ?? 0,
                                  preparedPoints: f.prepared_points ?? 0,
                                  seasonLength: f.season_length ?? 0,
                                })),
                              }
                            : undefined,
                        }
                      : undefined,
                  };
                  messageAuditId = auditId;
                  break;
                }

                default:
                  break;
              }
            });

            if (text.trim()) {
              const mid = `hist-asst-${msg.created_at}-${Math.random().toString(36).slice(2, 6)}`;
              messages.push({
                id: mid,
                role: 'assistant',
                content: text,
                timestamp: msg.created_at,
                auditId: messageAuditId,
              });
              timeline.push({ type: 'assistant', messageId: mid });
            }
          }
        });

        // Re-check: while we were fetching, the user may have either
        //   (a) sent a new message in this same thread (don't overwrite), or
        //   (b) navigated to a different thread (don't pollute the new one).
        const post = useCopilotStore.getState();
        if (post.threadId !== threadId) return;
        if (post.messages.length > 0) return;
        store.loadHistoryMessages(messages, artifacts, findings, timeline);
        Object.entries(auditReports).forEach(([id, report]) => {
          store.addAuditReport(report, id);
        });
      } catch (e) {
        console.error('[useCopilotHistory] Error loading history:', e);
      } finally {
        useCopilotStore.getState().setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [threadId]);
}
